import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { cmsApiConfig } from '../../../cmsConfig';
import {
  BerattigadTillStodText,
  ParticipationListItem,
} from '../../../types/Coach/ParticipationListItem';
import { CoachPageTexts } from '../../../types/Coach/CoachTexts';
import {
  EnhancedSupportSignOptions,
  ParticipantDetails,
} from '../../../types/Coach/ParticipantDetails';
import { ParticipationEndResultUpdate } from '../../../types/Coach/ParticipationEndResultUpdate';
import { EnhancedSupportModel } from '../../../types/Coach/Insats';
import { EnhancedSupportResponse } from '../../../types/Coach/EnhancedSupportResponse';
import { AdditionalSupportModel } from '../../../types/Coach/AdditionalSupportModel';
import { EducationListItem } from '../../../types/Coach/EducationListItem';
import { DecisionsOnPackagesTexts } from '../../../types/Enums/DecisionOnPackages';
import { setUnauthorized } from '../../../redux/reducer/authSlice';
import { HttpStatusCode } from '../../../constants/HttpStatusCode';
export interface ReactConfig {
  applicationInsightsConnectionString: string;
}

const baseCoachQuery = fetchBaseQuery({
  baseUrl: cmsApiConfig.url + '/coach',
  headers: {
    'X-CSRF': '1',
  },
  credentials: 'include',
});

const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseCoachQuery(args, api, extraOptions);

  if (result.error?.status === HttpStatusCode.Unauthorized) {
    api.dispatch(setUnauthorized());
  }

  return result;
};

export const coachApi = createApi({
  reducerPath: 'coachApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'CoachTexts',
    'NewParticipants',
    'OngoingParticipants',
    'FinishedParticipants',
    'ParticipantDetails',
    'SolutionTypes',
    'Educations',
  ],
  endpoints: (builder) => ({
    getCoachPageTexts: builder.query<CoachPageTexts, void>({
      query: () => ({
        url: `/getPageTexts`,
      }),
      providesTags: ['CoachTexts'],
    }),
    getNewParticipants: builder.query<ParticipationListItem[], void>({
      query: () => ({
        url: `/newParticipants`,
      }),
      providesTags: ['NewParticipants'],
      transformResponse: (response: ParticipationListItem[]) => {
        return response.map((item) => {
          const decisionOnPackage = item.decisionOnPackage
            ? item.decisionOnPackage
            : undefined;
          const decisionOnPackageText = decisionOnPackage
            ? decisionOnPackage
                .map(
                  (packageDecision) =>
                    DecisionsOnPackagesTexts[
                      packageDecision as keyof typeof DecisionsOnPackagesTexts
                    ]
                )
                .join(', ')
            : undefined;
          return {
            ...item,
            berattigadTillStodText:
              BerattigadTillStodText[
                item.berattigadTillStod as keyof typeof BerattigadTillStodText
              ],
            decisionOnPackageText: decisionOnPackageText,
          } as ParticipationListItem;
        });
      },
    }),
    getParticipantById: builder.query<ParticipantDetails, string>({
      query: (id) => ({
        url: `/participant?participantId=${id}`,
      }),
      transformResponse: (response: ParticipantDetails) => {
        return {
          ...response,
          berattigadTillStodText:
            response.berattigadTillStod !== undefined
              ? BerattigadTillStodText[
                  response.berattigadTillStod as keyof typeof BerattigadTillStodText
                ]
              : null,
        } as ParticipantDetails;
      },
      providesTags: (result, error, id) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    getOngoingParticipants: builder.query<ParticipationListItem[], void>({
      query: () => ({
        url: `/ongoingParticipants`,
      }),
      transformResponse: (response: ParticipationListItem[]) => {
        return response.map((item) => {
          const decisionOnPackage = item.decisionOnPackage
            ? item.decisionOnPackage
            : undefined;
          const decisionOnPackageText = decisionOnPackage
            ? decisionOnPackage
                .map(
                  (packageDecision) =>
                    DecisionsOnPackagesTexts[
                      packageDecision as keyof typeof DecisionsOnPackagesTexts
                    ]
                )
                .join(', ')
            : undefined;
          return {
            ...item,
            meetingsCount: item.meetingsCount ?? 0,
            decisionOnPackageText: decisionOnPackageText,
          } as ParticipationListItem;
        });
      },
      providesTags: ['OngoingParticipants'],
    }),
    getFinishedParticipants: builder.query<ParticipationListItem[], void>({
      query: () => ({
        url: `/finishedParticipants`,
      }),
      transformResponse: (response: ParticipationListItem[]) => {
        return response.map((item) => {
          const decisionOnPackage = item.decisionOnPackage
            ? item.decisionOnPackage
            : undefined;
          const decisionOnPackageText = decisionOnPackage
            ? decisionOnPackage
                .map(
                  (packageDecision) =>
                    DecisionsOnPackagesTexts[
                      packageDecision as keyof typeof DecisionsOnPackagesTexts
                    ]
                )
                .join(', ')
            : undefined;
          return {
            ...item,
            berattigadTillStodText:
              BerattigadTillStodText[
                item.berattigadTillStod as keyof typeof BerattigadTillStodText
              ],
            decisionOnPackageText: decisionOnPackageText,
          } as ParticipationListItem;
        });
      },
      providesTags: ['FinishedParticipants'],
    }),
    updateParticipantInformation: builder.mutation<
      void,
      {
        id: string;
        body: { needTranslator: boolean; languageId: string | null };
      }
    >({
      query: ({ id, body }) => ({
        url: `/participantInformation?participantId=${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => {
        return [{ type: 'ParticipantDetails', id }];
      },
    }),
    sendEndReport: builder.mutation<void, ParticipationEndResultUpdate>({
      query: (body) => ({
        url: '/participationEndResult',
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, body) => {
        return [{ type: 'ParticipantDetails', id: body.id }];
      },
    }),
    reopenParticipant: builder.mutation<void, string>({
      query: (participantId) => ({
        url: `/reopenParticipant?participantId=${participantId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, participantId) => {
        return [{ type: 'ParticipantDetails', id: participantId }];
      },
    }),
    requestAdditionalSupport: builder.mutation<
      void,
      { participantId: string; body: AdditionalSupportModel }
    >({
      query: ({ participantId, body }) => ({
        url: `/additionalSupport?participantId=${participantId}`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error, { participantId }) => {
        return [{ type: 'ParticipantDetails', id: participantId }];
      },
    }),
    createEnhancedSupportRequest: builder.mutation<
      EnhancedSupportResponse,
      { participantId: string; body: EnhancedSupportModel }
    >({
      query: ({ participantId, body }) => ({
        url: `/enhancedSupport?participantId=${participantId}`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error, { participantId }) => {
        return [{ type: 'ParticipantDetails', id: participantId }];
      },
    }),
    uploadEnhancedSupportSigningDocuments: builder.mutation<
      void,
      { participantId: string; enhancedSupportId: string; body: FormData }
    >({
      query: ({ participantId, enhancedSupportId, body }) => ({
        url: `/enhancedSupport/${enhancedSupportId}/signatureDocuments?participantId=${participantId}`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error, { participantId }) => {
        return [{ type: 'ParticipantDetails', id: participantId }];
      },
    }),
    resendEnhancedSupportSigning: builder.mutation<
      EnhancedSupportResponse,
      {
        participantId: string;
        enhancedSupportId: string;
        signType: EnhancedSupportSignOptions;
      }
    >({
      query: ({ participantId, enhancedSupportId, signType }) => ({
        url: `/enhancedSupport/resendSigning?participantId=${participantId}`,
        method: 'POST',
        body: {
          enhancedSupportId: enhancedSupportId,
          signType: signType,
        },
      }),
      invalidatesTags: (result, error, { participantId }) => {
        return [{ type: 'ParticipantDetails', id: participantId }];
      },
    }),
    getEducationList: builder.query<EducationListItem[], string>({
      query: (id) => ({
        url: `/educationList?participantId=${id}`,
      }),
      providesTags: ['Educations'],
    }),
  }),
});

export const {
  useGetCoachPageTextsQuery,
  useLazyGetCoachPageTextsQuery,
  useGetNewParticipantsQuery,
  useLazyGetNewParticipantsQuery,
  useGetOngoingParticipantsQuery,
  useLazyGetOngoingParticipantsQuery,
  useGetFinishedParticipantsQuery,
  useLazyGetFinishedParticipantsQuery,
  useGetParticipantByIdQuery,
  useLazyGetParticipantByIdQuery,
  useUpdateParticipantInformationMutation,
  useSendEndReportMutation,
  useReopenParticipantMutation,
  useRequestAdditionalSupportMutation,
  useCreateEnhancedSupportRequestMutation,
  useUploadEnhancedSupportSigningDocumentsMutation,
  useResendEnhancedSupportSigningMutation,
  useGetEducationListQuery,
  useLazyGetEducationListQuery,
} = coachApi;
